import React, { useState, useEffect, createRef } from 'react';
import { Link } from 'react-router-dom';
import BlockUi from 'react-block-ui';
import 'react-block-ui/style.css';
import { Colors } from '../../../../constants/Colors.js';
import LinkDownloadDashboard from '../../../../components/link/link-download-dashboard.jsx';
import AvancesFilters from '../../../../components/dashboards/home/avances-filters.jsx';
import Api from '../../../../api/global.service.js';
import env from '../../../../environment.js';
import SelectCiclo from '../../../../components/select/select-ciclo.jsx';


const FichasSidebar = ({ isVisible, onToggle, onFichaSelect, selectedFichaId }) => {
  const [fichas, setFichas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;
  const [error, setError] = useState(null);
  const [blockingAvances, setBlockingAvances] = useState(false);

  const [dataAvances, setDataAvances] = useState([]);
  const [dataAvancesTotal, setDataAvancesTotal] = useState([]);
  const avances = createRef();
  const [idCiclo, setIdCiclo] = useState(() => {
    const stored = parseInt(localStorage.getItem(`${env.appName}-ciclo_id`), 10);
    return Number.isNaN(stored) ? 5 : stored;
  });

  // Animación de cierre
  const [isClosing, setIsClosing] = useState(false);




  // Filtrar fichas basado en el término de búsqueda usando el servicio
  useEffect(() => {
    let isMounted = true;
    const abortController = new AbortController();
    
    const fetchData = async () => {
      try {
        await getAvanceSIC(null, isMounted, abortController.signal);
      } catch (error) {
        if (isMounted && !abortController.signal.aborted) {
          console.error('Error fetching avances:', error);
        }
      }
    };
    
    fetchData();
    
    // Cleanup function para cancelar la petición y marcar el componente como desmontado
    return () => {
      isMounted = false;
      abortController.abort();
    };
  }, [searchTerm]);

  const handleFichaClick = (ficha) => {
    if (onFichaSelect) {
      onFichaSelect(ficha);
    }
    // Cerrar con animación local (sin toggle) para no reabrir el sidebar
    closeWithAnimation();
  };

  // Cerrar con animación local sin cambiar visibilidad en el padre
  const closeWithAnimation = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsClosing(false);
    }, 300);
  };

  // Cerrar con animación y luego togglear (para overlay y botón cerrar)
  const closeWithAnimationToggle = () => {
    if (typeof onToggle !== 'function') return;
    setIsClosing(true);
    setTimeout(() => {
      onToggle();
      setIsClosing(false);
    }, 300);
  };

  const getEstadoColor = (estado) => {
    switch (estado) {
      case 'Completada':
        return '#4caf50';
      case 'En progreso':
        return '#ff9800';
      case 'Pendiente':
        return '#f44336';
      default:
        return '#757575';
    }
  };

  if (!isVisible && !isClosing) {
    return null;
  }

  const getAvanceSIC = async (value, isMounted, signal) => {
    // Verificar si el componente sigue montado antes de actualizar estado
    if (!isMounted) return;
    
    setBlockingAvances(true);

    try {
      const response = await Api.search('dashboards/avances/sic', {
        filter: [
          {
            column: 'id_ciclo',
            operator: '=',
            value: value?.target ? value.target.id : idCiclo,
          },
        ],
      }, { signal }); // Pasar el signal para cancelar la petición

      // Verificar nuevamente si el componente sigue montado y la petición no fue cancelada
      if (isMounted && !signal?.aborted && response.status == 200) {
        setDataAvances(response.data);
        setDataAvancesTotal(response.data);
      }
    } catch (error) {
      if (isMounted && !signal?.aborted) {
        console.error('Error in getAvanceSIC:', error);
      }
    } finally {
      // Solo actualizar estado si el componente sigue montado y la petición no fue cancelada
      if (isMounted && !signal?.aborted) {
        setBlockingAvances(false);
      }
    }
  };

  const handleCicloChange = (filter) => {
    const target = filter?.target;
    if (target && target.hasOwnProperty('id')) {
      setIdCiclo(target.id);
    }
    setCurrentPage(1);
    getAvanceSIC(filter, true);
  };

  // Datos filtrados y paginación
  const registros = (dataAvances || []).filter(
    (avance) => (avance.ficha_title || '').toUpperCase() === 'FICHA SIC'
  );
  const registrosFiltrados = registros.filter((avance) => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return true;
    return (
      String(avance.nombre_productor || '').toLowerCase().includes(term) ||
      String(avance.nombre_parcela || '').toLowerCase().includes(term) ||
      String(avance.nombre_inspector || '').toLowerCase().includes(term) ||
      String(avance.inspeccion_fecha || '').toLowerCase().includes(term)
    );
  });
  const totalRegistros = registrosFiltrados.length;
  const totalPaginas = Math.max(1, Math.ceil(totalRegistros / pageSize));
  const paginaActual = Math.min(currentPage, totalPaginas);
  const inicio = (paginaActual - 1) * pageSize;
  const visibles = registrosFiltrados.slice(inicio, inicio + pageSize);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const goToPage = (p) => {
    if (p < 1 || p > totalPaginas) return;
    setCurrentPage(p);
  };

  return (
    <div>
      {/* Overlay para cerrar al hacer clic fuera */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          backgroundColor: 'rgba(0, 0, 0, 0.4)',
          zIndex: 9998,
          cursor: 'pointer',
          opacity: isClosing ? 0 : 1,
          transition: 'opacity 200ms ease',
        }}
        onClick={closeWithAnimationToggle}
      />
      
      {/* Modal centrado */}
      <div
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '85vw',
          maxWidth: '1000px',
          height: '80vh',
          backgroundColor: '#ffffff',
          borderRadius: '12px',
          boxShadow: '0 10px 25px rgba(0,0,0,0.15)',
          zIndex: 9999,
          display: 'flex',
          flexDirection: 'column',
          border: '1px solid #e0e0e0',
          overflow: 'hidden',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header del modal */}
        <div
          style={{
            padding: '16px 20px',
            borderBottom: '1px solid #e0e0e0',
            backgroundColor: '#f8f9fa',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <h5 style={{ margin: 0, fontWeight: 600, color: '#333' }}>Últimos registros</h5>
          <button
            onClick={closeWithAnimationToggle}
            style={{
              background: 'transparent',
              border: '1px solid #ddd',
              borderRadius: '8px',
              fontSize: '14px',
              cursor: 'pointer',
              color: '#333',
              padding: '6px 10px',
            }}
            title="Cerrar"
          >
            Cerrar
          </button>
        </div>

        {/* Controles de búsqueda y resumen */}
        <div style={{ padding: '12px 20px', borderBottom: '1px solid #eee', backgroundColor: '#fff' }}>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            <SelectCiclo setFilter={handleCicloChange} acopio={0} width="160px" />
            <input
              type="text"
              className="form-control"
              placeholder="Buscar por productor, parcela, inspector o fecha..."
              value={searchTerm}
              onChange={handleSearchChange}
              style={{ maxWidth: '380px' }}
            />
            <div style={{ marginLeft: 'auto', color: '#666' }}>
              Total de fichas: {totalRegistros}
            </div>
          </div>
        </div>

        {/* Cuerpo con tabla y paginación */}
        <BlockUi
          tag="div"
          blocking={blockingAvances}
          keepInView={true}
          style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}
        >
          <div style={{ flex: 1, overflow: 'auto', padding: '12px', minHeight: 0 }}>
            <table className="table table-striped table-hover table-bordered" style={{ marginBottom: 0 }}>
              <thead style={{ position: 'sticky', top: 0, backgroundColor: '#fff', zIndex: 1 }}>
                <tr className="text-center" style={{ backgroundColor: '#9f9e9eff', color: '#fff' }}>
                    <th>IdFicha</th>
                  <th>Productor</th>
                  <th>Parcela</th>
                  <th>Inspector</th>
                  <th>Fecha</th>
                  <th style={{ width: '120px' }}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {visibles.map((avance, i) => {
                  const isSelected = selectedFichaId === avance.uuid;
                  return (
                    <tr key={avance.id_api_ficha || i} style={{ backgroundColor: isSelected ? '#e3f2fd' : 'transparent' }}>
                                            <td>{avance.id_api_ficha}</td>
                      <td>
                        <span className="text-capitalize">{String(avance.nombre_productor || '').toLowerCase()}</span>
                      </td>
                      <td>{avance.nombre_parcela}</td>
                      <td>{avance.nombre_inspector}</td>
                      <td>{avance.inspeccion_fecha}</td>
                      <td>
                        <button
                          className={"btn btn-sirio btn-sm" + (avance.id_kobo_ficha_principal ? "" : " btn-outline-secondary")}
                          onClick={() => handleFichaClick(avance)}
                          disabled={!avance.id_kobo_ficha_principal}
                          style={{ borderColor: isSelected ? Colors.primary_500 : '#ddd', color: isSelected ? Colors.primary_500 : Colors.primary_700 }}
                        >
                          Seleccionar
                        </button>
                      </td>
                    </tr>
                  );
                })}
                {visibles.length === 0 && (
                  <tr>
                    <td colSpan={5} style={{ textAlign: 'center', color: '#666', padding: '24px' }}>
                      No hay registros para mostrar
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </BlockUi>

        {/* Paginación */}
        <div style={{ padding: '12px 20px', borderTop: '1px solid #eee', backgroundColor: '#fff', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <button className="btn btn-light btn-sm" onClick={() => goToPage(paginaActual - 1)} disabled={paginaActual <= 1}>
            ← Anterior
          </button>
          <span style={{ color: '#666' }}>Página {paginaActual} de {totalPaginas}</span>
          <button className="btn btn-light btn-sm" onClick={() => goToPage(paginaActual + 1)} disabled={paginaActual >= totalPaginas}>
            Siguiente →
          </button>
        </div>
      </div>
    </div>
  );
};

export default FichasSidebar;